/*
 * AMASS event offer form (sections/amass-event-offer.liquid)
 * ----------------------------------------------------------------------------
 * On load:
 *   Picks the active offer tier from the section's JSON (tier 1 until its
 *   ends_at, then tier 2, then "ended") against the shopper's clock, and swaps
 *   the headline, subhead, stamp, button labels, fine print, grid heading, the
 *   on-screen code and the /discount/<code> link to match. Shopify's CDN caches
 *   the rendered page, so Liquid cannot do a same-day switch reliably.
 * On submit:
 *   1. Validates client-side: email format, US phone (10 digits), email consent
 *      ticked (the code is delivered by email). SMS consent is optional.
 *   2. Records the lead in Klaviyo with the PUBLIC client APIs (company id from
 *      the theme's Klaviyo setting; no private key in the browser):
 *        a. /client/subscriptions → joins the event list with email marketing
 *           consent and the phone number on the profile.
 *        b. /client/subscriptions again, only if the SMS box is ticked → adds
 *           SMS marketing consent. Kept separate so an account without SMS
 *           enabled still records the email subscription.
 *        c. /client/events → logs "<event> Code Requested" with the active
 *           code and percent as properties. The Klaviyo flow that emails the
 *           code triggers on THIS metric and reads event.Code, so one flow
 *           serves both tiers and a re-submit re-sends the code.
 *   3. Shows the inline success state with the code and the shop button.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // 2025-04-15 or later: the client subscriptions API only accepts the
  // explicit `subscriptions` consent block from this revision on (verified
  // 2026-10-14; 2024-10-15 rejects it with 400).
  var KLAVIYO_REVISION = '2025-04-15';

  /* ---------- offer tiers ---------- */

  function readTiers(root) {
    var el = root.querySelector('[data-evo-tiers]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function activeTier(config, now) {
    if (!config || !config.tiers) return null;
    for (var i = 0; i < config.tiers.length; i++) {
      var t = config.tiers[i];
      if (!t.code) continue;
      var ends = t.ends_at ? Date.parse(t.ends_at) : NaN;
      if (isNaN(ends) || now <= ends) return t;
    }
    return null;
  }

  function setText(root, selector, value) {
    if (value === undefined || value === null || value === '') return;
    root.querySelectorAll(selector).forEach(function (el) { el.textContent = value; });
  }

  function applyTier(root, tier, config) {
    var form = root.querySelector('[data-evo-form]');
    var ended = root.querySelector('[data-evo-ended]');
    var stamp = root.querySelector('.evo__stamp');
    if (!tier) {
      if (form) form.hidden = true;
      if (stamp) stamp.hidden = true;
      if (ended) {
        setText(ended, '.evo__success-h', config && config.ended_heading);
        setText(ended, '.evo__success-p', config && config.ended_body);
        ended.hidden = false;
      }
      setText(root, '[data-evo-heading]', config && config.ended_heading);
      root.classList.add('is-ended');
      return;
    }
    var pct = tier.percent + '%';
    setText(root, '[data-evo-heading]', tier.heading);
    setText(root, '[data-evo-subhead]', tier.subhead);
    setText(root, '[data-evo-fine]', tier.fine_print);
    setText(root, '[data-evo-grid-heading]', tier.grid_heading);
    setText(root, '[data-evo-percent]', pct);
    setText(root, '[data-evo-code-out]', tier.code);
    setText(root, '[data-evo-submit]', 'Get my ' + pct + ' off');
    setText(root, '[data-evo-sticky-label]', 'Get my ' + pct + ' off');
    setText(root, '[data-evo-success-cta]', 'Shop with ' + pct + ' off');
    var shop = root.getAttribute('data-shop-link') || '/pages/shop';
    root.querySelectorAll('[data-evo-success-cta]').forEach(function (a) {
      a.setAttribute('href', '/discount/' + encodeURIComponent(tier.code) + '?redirect=' + encodeURIComponent(shop));
    });
    root.__evoTier = tier;
  }

  /* ---------- validation ---------- */

  function labelFor(input) {
    var wrap = input.closest('.evo-field');
    var label = wrap && wrap.querySelector('.evo-label');
    return (label ? label.textContent : (input.name || 'This field')).replace(/\*/g, '').trim();
  }

  function setError(form, input, message) {
    input.setAttribute('aria-invalid', 'true');
    var el = form.querySelector('[data-evo-error-for="' + input.name + '"]');
    if (el) { el.textContent = message; el.hidden = false; }
  }

  function clearError(form, input) {
    input.removeAttribute('aria-invalid');
    var el = form.querySelector('[data-evo-error-for="' + input.name + '"]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  // US numbers only (the event is in Los Angeles). Returns E.164 or ''.
  function normalizePhone(raw) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length !== 10) return '';
    return '+1' + digits;
  }

  function validate(form) {
    var first = null;
    form.querySelectorAll('[data-evo-required]').forEach(function (input) {
      clearError(form, input);
      if (!(input.value || '').trim()) {
        setError(form, input, labelFor(input) + ' is required.');
        if (!first) first = input;
      }
    });
    var email = form.querySelector('[data-evo-email]');
    if (email) {
      var v = (email.value || '').trim();
      if (v && !EMAIL_RE.test(v)) {
        setError(form, email, 'Enter a valid email address.');
        if (!first) first = email;
      }
    }
    var phone = form.querySelector('[data-evo-phone]');
    if (phone) {
      var p = (phone.value || '').trim();
      if (p && !normalizePhone(p)) {
        setError(form, phone, 'Enter a 10-digit US phone number.');
        if (!first) first = phone;
      }
    }
    var consent = form.querySelector('[data-evo-email-consent]');
    if (consent) {
      clearError(form, consent);
      if (!consent.checked) {
        setError(form, consent, 'We need your OK to email you the code.');
        if (!first) first = consent;
      }
    }
    if (first) { try { first.focus(); } catch (e) { /* noop */ } }
    return !first;
  }

  function collect(form) {
    var data = {};
    form.querySelectorAll('input').forEach(function (input) {
      if (!input.name) return;
      if (input.type === 'checkbox') { data[input.name] = !!input.checked; return; }
      data[input.name] = (input.value || '').trim();
    });
    return data;
  }

  /* ---------- Klaviyo ---------- */

  function klaviyoPost(path, company, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(payload)
    }).then(function (res) { return res.ok; }).catch(function () { return false; });
  }

  function subscribe(company, list, attributes, source) {
    if (!list) return Promise.resolve(false);
    return klaviyoPost('subscriptions', company, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: { list: { data: { type: 'list', id: list } } }
      }
    });
  }

  function track(company, metric, profile, props) {
    return klaviyoPost('events', company, {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: profile } },
          properties: props
        }
      }
    });
  }

  function onsiteIdentify(data) {
    try {
      var kl = window.klaviyo || window._learnq;
      if (kl && typeof kl.push === 'function') kl.push(['identify', { $email: data.email, $first_name: data.first_name, $last_name: data.last_name }]);
    } catch (e) { /* noop */ }
  }

  /* ---------- UI ---------- */

  function setBusy(form, busy) {
    var btn = form.querySelector('[data-evo-submit]');
    if (!btn) return;
    btn.disabled = busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) { btn.dataset.label = btn.textContent; btn.textContent = 'Sending…'; }
    else if (btn.dataset.label) { btn.textContent = btn.dataset.label; }
  }

  function showStatus(form, message, isError) {
    var status = form.querySelector('[data-evo-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', !!isError);
    status.hidden = !message;
  }

  function succeed(root, form, email) {
    var success = root.querySelector('[data-evo-success]');
    root.classList.add('is-submitted');
    if (success) {
      success.querySelectorAll('[data-evo-email-out]').forEach(function (el) { el.textContent = email; });
      success.hidden = false;
      try { success.setAttribute('tabindex', '-1'); success.focus(); } catch (e) { /* noop */ }
    }
    form.hidden = true;
    try { root.dispatchEvent(new CustomEvent('evo:submitted')); } catch (e) { /* noop */ }
    try { if (window.dataLayer) window.dataLayer.push({ event: 'event_code_requested' }); } catch (e) { /* noop */ }
  }

  function bindSticky(root) {
    var sticky = root.querySelector('[data-evo-sticky]');
    var card = root.querySelector('#code');
    if (!sticky || !card || sticky.__evoBound) return;
    sticky.__evoBound = true;
    root.classList.add('has-sticky');
    var cardVisible = false;
    function update() {
      sticky.hidden = cardVisible || root.classList.contains('is-submitted') || root.classList.contains('is-ended');
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        cardVisible = entries[0].isIntersecting;
        update();
      }, { threshold: 0.15 }).observe(card);
    } else {
      sticky.hidden = true;
    }
    root.addEventListener('evo:submitted', update);
  }

  function bind(root) {
    var config = readTiers(root);
    applyTier(root, activeTier(config, Date.now()), config);
    bindSticky(root);

    var form = root.querySelector('[data-evo-form]');
    if (!form || form.__evoBound) return;
    form.__evoBound = true;

    var company = root.getAttribute('data-klaviyo-company') || '';
    var list = root.getAttribute('data-klaviyo-list') || '';
    var metric = root.getAttribute('data-klaviyo-event') || 'Event Code Requested';
    var source = root.getAttribute('data-klaviyo-source') || 'Event offer page';
    var eventName = root.getAttribute('data-event-name') || '';

    form.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.getAttribute && t.getAttribute('aria-invalid') === 'true') clearError(form, t);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      showStatus(form, '', false);
      if (!validate(form)) return;

      var data = collect(form);
      var tier = root.__evoTier || activeTier(config, Date.now());
      // Honeypot: bots fill every field; humans never see this one.
      if (data.website) { succeed(root, form, data.email); return; }
      if (!company) {
        showStatus(form, 'This form isn’t connected yet. Email hello@amass.com and we’ll send your code by hand.', true);
        return;
      }
      if (!tier) {
        showStatus(form, 'This offer has ended.', true);
        return;
      }

      var phone = normalizePhone(data.phone);
      var profile = {
        email: data.email,
        phone_number: phone || undefined,
        first_name: data.first_name || undefined,
        last_name: data.last_name || undefined,
        properties: {
          'Event': eventName,
          'Event Signup Source': source,
          'Event Code': tier.code,
          'SMS Consent Requested': !!data.sms_consent
        }
      };
      var emailAttrs = {
        email: data.email,
        phone_number: phone || undefined,
        first_name: data.first_name || undefined,
        last_name: data.last_name || undefined,
        properties: profile.properties,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      var smsAttrs = {
        email: data.email,
        phone_number: phone,
        subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      var props = {
        'Code': tier.code,
        'Percent Off': tier.percent,
        'Event': eventName,
        'Source': source,
        'SMS Consent': !!data.sms_consent,
        'Page': window.location.href
      };

      setBusy(form, true);
      Promise.all([
        track(company, metric, profile, props),
        subscribe(company, list, emailAttrs, source),
        (data.sms_consent && phone) ? subscribe(company, list, smsAttrs, source + ' (SMS)') : Promise.resolve(false)
      ]).then(function (results) {
        if (results[0] || results[1]) {
          onsiteIdentify(data);
          succeed(root, form, data.email);
        } else {
          setBusy(form, false);
          showStatus(form, 'Something went wrong sending your details. Please try again in a moment.', true);
        }
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-evo-root]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
})();
